<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/monitoring_campaign_verifications/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.monitoringCampaignVerifications')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
    <action
      v-if="canRefreshMonitoring"
      :icon="AddIcon"
      :label="$t('pages.monitoringCampaignVerification.refreshButton')"
      :busy="refreshing"
      @click="refreshMonitoring"
    />
  </toolbar>
  <div class="table ml-12">
    <data-table
      class="mb-auto"
      v-model:sort-by="sortKey"
      v-model:sort-direction="sortDirection"
      :busy="loading"
      :items="records"
      :columns="columns"
      :visible-columns="visibleColumns"
    />
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { computed, reactive, unref } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import AddIcon from '@images/panes/add.svg?component';
import Action from '@components/utils/toolbar/Action.vue';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import { useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { PagedResult, MonitoringCampaignVerification } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import { useTableFields } from '@hooks/table-configuration';
import { useStore } from '@/store';
import { useProvider } from '@components/utils/table/hooks/provider';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'campaignStartDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();
const notify = useNotify();
const store = useStore();
const canRefreshMonitoring = computed(() =>
  store.state.auth.user?.roles?.includes('ROLE_DEVELOPER') ?? false);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const {
  data: items,
  loading,
  refetch,
} = useProvider<PagedResult<MonitoringCampaignVerification>>(
  '/monitoring_campaign_verifications',
  providerParams,
);

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    visible: true,
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'campaignStartDate',
    title: t('pages.monitoringCampaignVerification.campaignStartDate'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : '-'),
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('pages.monitoringCampaignVerification.campaignStartDate'),
      },
    },
  },
  {
    key: 'campaignName',
    title: t('pages.monitoringCampaignVerification.campaignName'),
    sortable: true,
    visible: true,
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    to: (row: MonitoringCampaignVerification) => ({
      name: 'campaign-group-details',
      params: { campaignGroupId: row.campaignGroupId },
      query: {
        globalFilter: row.regionId
          ? encodeURIComponent(JSON.stringify({ regions: [row.regionId] }))
          : undefined,
      },
    }),
    filter: {
      type: 'text',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('pages.monitoringCampaignVerification.campaignName'),
      },
    },
  },
  {
    key: 'region',
    title: t('region'),
    sortable: true,
    sortKey: 'campaign.region.name',
    visible: true,
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      key: 'campaign.region.name',
      type: 'text',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('region'),
      },
    },
  },
  {
    key: 'totalDrivers',
    title: t('pages.monitoringCampaignVerification.totalDrivers'),
    sortable: true,
    visible: true,
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'number',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('pages.monitoringCampaignVerification.totalDrivers'),
      },
    },
  },
  {
    key: 'missingVerifications',
    title: t('pages.monitoringCampaignVerification.missingVerifications'),
    sortable: true,
    visible: true,
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'number',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('pages.monitoringCampaignVerification.missingVerifications'),
      },
    },
  },
  {
    key: 'verificationPercentage',
    title: t('pages.monitoringCampaignVerification.verificationPercentage'),
    sortable: true,
    visible: true,
    formatter: (v: number) => (v !== undefined ? `${v}%` : '-'),
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'number',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('pages.monitoringCampaignVerification.verificationPercentage'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.monitoringCampaignVerification.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.monitoringCampaignVerification.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const [execRefresh, { loading: refreshing }] = useFetchAction();

const refreshMonitoring = async () => {
  try {
    await execRefresh('/monitoring_campaign_verifications/refresh', { method: 'POST' });
    notify?.({ variant: 'success', title: t('pages.monitoringCampaignVerification.refreshSuccess') });
    refetch();
  } catch {
    notify?.({ variant: 'error', title: t('pages.monitoringCampaignVerification.refreshError') });
  }
};

const records = computed(() => unref(items)?.items ?? []);
const total = computed(() => unref(items)?.total ?? 0);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  }),
);
</script>

<style lang="scss" scoped>
.table {
  @apply flex flex-col;
}
</style>
